// src/components/common/StaffMemberById.jsx
//
// <StaffMember> for records that only store a staff id (created_by,
// deactivated_by, …). Names come from the department's staff list, which every
// staff member may read, fetched once per session. Someone from another
// department is looked up on their own, which needs staff:read.
import React, { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import StaffMember from './StaffMember';
import { staffAPI } from '../../features/staff/services/staff';
import { tokenService } from '../../features/auth/services/tokenService';

let departmentStaff = null; // Promise<Map staffId → staff member>
let loadedFor = null; // department the list belongs to, as switching user can change it
const others = new Map(); // staffId → Promise<staff member | null>

function lookup(staffId) {
  const departmentId = tokenService.getDepartmentId();
  if (!departmentStaff || loadedFor !== departmentId) {
    loadedFor = departmentId;
    departmentStaff = staffAPI.getDepartmentStaff({ include_inactive: true })
      .then((res) => new Map((res.data?.staff_members ?? []).map((s) => [s.staff_member_id, s])))
      .catch(() => {
        departmentStaff = null; // try again next time
        return new Map();
      });
  }
  return departmentStaff.then((byId) => {
    if (byId.has(staffId)) return byId.get(staffId);
    if (!tokenService.hasScope('staff:read')) return null;
    if (!others.has(staffId)) {
      others.set(
        staffId,
        staffAPI.getStaffById(staffId)
          .then((res) => res.data?.data ?? res.data ?? null)
          .catch(() => null),
      );
    }
    return others.get(staffId);
  });
}

export default function StaffMemberById({ staffId, avatarSize = 24, gap = 1.5, nameVariant = 'body2', ...props }) {
  const { t } = useTranslation(['common']);
  const [staffMember, setStaffMember] = useState(undefined); // undefined = loading, null = not found

  useEffect(() => {
    let cancelled = false;
    setStaffMember(undefined);
    if (staffId) lookup(staffId).then((s) => { if (!cancelled) setStaffMember(s); });
    return () => { cancelled = true; };
  }, [staffId]);

  if (!staffId || staffMember === null) {
    return <Typography variant={nameVariant}>{t('common:labels.unknown')}</Typography>;
  }
  if (staffMember === undefined) return <Typography variant={nameVariant} color="text.disabled">…</Typography>;
  return <StaffMember staffMember={staffMember} avatarSize={avatarSize} gap={gap} nameVariant={nameVariant} {...props} />;
}
